import React from "react";
import { css } from "glamor";
import { FaChevronDown } from "react-icons/fa";
import formatDate from "../../utils/formatDate";
import { toBRLCurrency } from "../../utils/formatCurrency";
import { purchaseStatusVariant } from "./financePurchasesHelpers";

// 5-column grid: PRODUTO | DATA | VALOR | ACESSO ATÉ | STATUS
const COLS = "minmax(0, 1fr) 100px 90px 105px 105px";

const headerRowClass = css({
  display: "grid",
  gridTemplateColumns: COLS,
  gap: 12,
  padding: "10px 18px",
  background: "#F8FAFC",
  color: "#94A3B8",
  fontSize: 11,
  fontWeight: 500,
  lineHeight: "16px",
  textTransform: "uppercase",
  letterSpacing: "0.02em",
  "@media(max-width: 640px)": { display: "none" },
});

const tableRowClass = css({
  display: "grid",
  gridTemplateColumns: COLS,
  gap: 12,
  padding: "14px 18px",
  borderTop: "0.6px solid #F1F5F9",
  alignItems: "center",
  cursor: "pointer",
  transition: "background 0.1s",
  ":hover": { background: "#F8FAFC" },
  "@media(max-width: 640px)": { display: "none" },
});

const productNameClass = css({
  color: "#0F172A",
  fontSize: 13,
  fontWeight: 500,
  lineHeight: "20px",
  overflowWrap: "anywhere",
  margin: 0,
});

const productCodeClass = css({
  color: "#94A3B8",
  fontSize: 11,
  fontWeight: 400,
  lineHeight: "16px",
  marginTop: 2,
});

const cellClass = css({
  color: "#475569",
  fontSize: 13,
  fontWeight: 400,
  lineHeight: "20px",
  minWidth: 0,
});

const valueClass = css(cellClass, {
  fontWeight: 500,
  color: "#0F172A",
});

const dashClass = css({
  color: "#CBD5E1",
  fontSize: 13,
});

const statusBadgeClass = (variant) =>
  css({
    display: "inline-flex",
    alignItems: "center",
    height: 22,
    padding: "0 8px",
    borderRadius: 4,
    border: `0.6px solid ${variant.border}`,
    background: variant.background,
    color: variant.color,
    fontSize: 11,
    fontWeight: 500,
    lineHeight: "16px",
    whiteSpace: "nowrap",
  });

// ─── Paginação ────────────────────────────────────────────────────────────────

const paginationClass = css({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: 4,
  padding: "12px 18px",
  borderTop: "0.6px solid #F1F5F9",
});

const pageButtonClass = (active) =>
  css({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 32,
    height: 32,
    padding: "0 6px",
    border: active ? "0.6px solid #0F172A" : "0.6px solid #E2E8F0",
    borderRadius: 6,
    background: active ? "#0F172A" : "#FFFFFF",
    color: active ? "#FFFFFF" : "#64748B",
    fontSize: 12,
    fontWeight: active ? 600 : 400,
    cursor: active ? "default" : "pointer",
    ":hover": { background: active ? "#0F172A" : "#F8FAFC" },
    ":disabled": { opacity: 0.4, cursor: "not-allowed" },
  });

// ─── Mobile ───────────────────────────────────────────────────────────────────

const mobileCardClass = css({
  display: "none",
  "@media(max-width: 640px)": {
    display: "block",
    padding: "14px",
    borderTop: "0.6px solid #F1F5F9",
    cursor: "pointer",
    transition: "background 0.1s",
    ":active": { background: "#F8FAFC" },
  },
});

const mobileTopRowClass = css({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 10,
  marginBottom: 10,
});

const mobileMetaRowClass = css({
  display: "flex",
  flexWrap: "wrap",
  gap: "8px 20px",
  marginBottom: 12,
});

const mobileMetaItemClass = css({
  display: "flex",
  flexDirection: "column",
  gap: 2,
});

const mobileMetaLabelClass = css({
  color: "#94A3B8",
  fontSize: 11,
  fontWeight: 400,
  lineHeight: "16px",
});

const mobileMetaValueClass = css({
  color: "#475569",
  fontSize: 13,
  fontWeight: 400,
  lineHeight: "20px",
});

const mobileMetaValueBoldClass = css({
  color: "#0F172A",
  fontSize: 13,
  fontWeight: 500,
  lineHeight: "20px",
});

// ─── Componentes ──────────────────────────────────────────────────────────────

export function PaginationBar({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;

  const pages = [];
  const start = Math.max(1, page - 2);
  const end = Math.min(totalPages, page + 2);
  for (let i = start; i <= end; i++) pages.push(i);

  return (
    <div className={paginationClass}>
      <button
        type="button"
        className={pageButtonClass(false)}
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        aria-label="Página anterior"
      >
        <FaChevronDown style={{ transform: "rotate(90deg)" }} size={10} />
      </button>

      {start > 1 && (
        <>
          <button type="button" className={pageButtonClass(false)} onClick={() => onPageChange(1)}>
            1
          </button>
          {start > 2 && <span style={{ color: "#94A3B8", fontSize: 12 }}>…</span>}
        </>
      )}

      {pages.map((p) => (
        <button
          key={p}
          type="button"
          className={pageButtonClass(p === page)}
          onClick={() => p !== page && onPageChange(p)}
        >
          {p}
        </button>
      ))}

      {end < totalPages && (
        <>
          {end < totalPages - 1 && <span style={{ color: "#94A3B8", fontSize: 12 }}>…</span>}
          <button
            type="button"
            className={pageButtonClass(false)}
            onClick={() => onPageChange(totalPages)}
          >
            {totalPages}
          </button>
        </>
      )}

      <button
        type="button"
        className={pageButtonClass(false)}
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
        aria-label="Próxima página"
      >
        <FaChevronDown style={{ transform: "rotate(-90deg)" }} size={10} />
      </button>
    </div>
  );
}

export function PurchaseTableHeader() {
  return (
    <div className={headerRowClass}>
      <span>Produto</span>
      <span>Data</span>
      <span>Valor</span>
      <span>Acesso até</span>
      <span>Status</span>
    </div>
  );
}

export default function PurchaseRow({ purchase, onSelect }) {
  const status = purchaseStatusVariant(purchase);
  const code = purchase.transactionCode || null;
  const dateText = purchase.createdAt ? formatDate(purchase.createdAt, "DD/MM/YYYY") : null;
  const accessText = purchase.coursesUserExpiresAt
    ? formatDate(purchase.coursesUserExpiresAt, "DD/MM/YYYY")
    : null;

  const handleClick = () => onSelect(purchase);
  const handleKeyDown = (e) => e.key === "Enter" && onSelect(purchase);
  const ariaLabel = `Ver detalhes: ${purchase.productName}`;

  return (
    <>
      {/* Desktop row */}
      <div
        className={tableRowClass}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel}
      >
        <div>
          <p className={productNameClass}>{purchase.productName || "Produto"}</p>
          {code && <span className={productCodeClass}>{code}</span>}
        </div>

        <span className={cellClass}>
          {dateText || <span className={dashClass}>—</span>}
        </span>

        <span className={valueClass}>{toBRLCurrency(purchase.productPrice)}</span>

        <span className={cellClass}>
          {accessText || <span className={dashClass}>—</span>}
        </span>

        <span>
          <span className={statusBadgeClass(status)}>{status.text}</span>
        </span>
      </div>

      {/* Mobile card */}
      <div
        className={mobileCardClass}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel}
      >
        <div className={mobileTopRowClass}>
          <div>
            <p className={productNameClass} style={{ marginBottom: 2 }}>
              {purchase.productName || "Produto"}
            </p>
            {code && <span className={productCodeClass}>{code}</span>}
          </div>
          <span className={statusBadgeClass(status)} style={{ flexShrink: 0 }}>
            {status.text}
          </span>
        </div>

        <div className={mobileMetaRowClass}>
          <div className={mobileMetaItemClass}>
            <span className={mobileMetaLabelClass}>Data</span>
            <span className={mobileMetaValueClass}>{dateText || "—"}</span>
          </div>
          <div className={mobileMetaItemClass}>
            <span className={mobileMetaLabelClass}>Valor</span>
            <span className={mobileMetaValueBoldClass}>{toBRLCurrency(purchase.productPrice)}</span>
          </div>
          {accessText && (
            <div className={mobileMetaItemClass}>
              <span className={mobileMetaLabelClass}>Acesso até</span>
              <span className={mobileMetaValueClass}>{accessText}</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
